import React from 'react';

// Live fishbone drawing built from the Fishbone sheet answers (focus + 6 bones x 5 items)
const SPINE_Y = 260;
const BONES = [250, 450, 650].flatMap((x) => [{ x, up: true }, { x, up: false }]);
const cut = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '');

const wrap = (text, width, maxLines) => {
  const words = (text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  words.forEach((w) => {
    const last = lines[lines.length - 1];
    if (last && `${last} ${w}`.length <= width) lines[lines.length - 1] = `${last} ${w}`;
    else lines.push(w);
  });
  return lines.length > maxLines ? [...lines.slice(0, maxLines - 1), `${cut(lines.slice(maxLines - 1).join(' '), width)}`] : lines;
};

const Bone = ({ bone, group, index }) => {
  const endY = bone.up ? 70 : 450;
  const endX = bone.x - 140;
  const items = (group?.rows || []).map((r) => r?.text).slice(0, 5);
  const title = group?.title;
  return (
    <g data-testid={`fish-bone-${index}`}>
      <line x1={bone.x} y1={SPINE_Y} x2={endX} y2={endY} stroke="#6b3a5e" strokeWidth="4" strokeLinecap="round" />
      <text x={endX} y={bone.up ? endY - 16 : endY + 30} textAnchor="middle" className="font-body" fontSize="17" fontWeight="700" fill={title ? '#6b3a5e' : '#c9b8c6'} letterSpacing="1">
        {cut(title ? title.toUpperCase() : `CATEGORY ${index + 1}`, 18)}
      </text>
      {[0, 1, 2, 3, 4].map((k) => {
        const t = 0.2 + k * 0.16;
        const px = bone.x - 140 * t;
        const py = SPINE_Y + (endY - SPINE_Y) * t;
        const txt = items[k];
        return (
          <g key={k}>
            <line x1={px + 4} y1={py} x2={px + 150} y2={py} stroke={txt ? '#8a6a5a' : '#e2d6df'} strokeWidth="1.5" />
            {txt && (
              <text x={px + 8} y={py - 5} fontSize="16" fill="#3f3f46" className="font-body">
                <title>{txt}</title>{cut(txt, 18)}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
};

export default function FishboneDiagram({ data }) {
  const focusLines = wrap(data.focus, 15, 3);
  const bones = data.bones || [];
  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-3 overflow-x-auto lg:w-[920px]" data-testid="fishbone-diagram">
      <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#6b3a5e] px-2 pt-1">My fishbone</p>
      <p className="font-body text-xs text-stone-500 px-2 mb-1">Fills in as you type below.</p>
      <svg viewBox="0 0 1000 520" className="w-full min-w-[620px] h-auto" role="img" aria-label="Fishbone diagram">
        <polygon points="30,200 95,260 30,320" fill="#efe6ee" stroke="#6b3a5e" strokeWidth="3" strokeLinejoin="round" />
        <line x1="90" y1={SPINE_Y} x2="790" y2={SPINE_Y} stroke="#6b3a5e" strokeWidth="6" strokeLinecap="round" />
        {BONES.map((b, i) => <Bone key={i} bone={b} group={bones[i]} index={i} />)}
        <path d="M790,170 Q975,180 980,260 Q975,340 790,350 Z" fill="#efe6ee" stroke="#6b3a5e" strokeWidth="3" />
        <circle cx="815" cy="215" r="6" fill="#6b3a5e" />
        <text x="890" y={SPINE_Y - (focusLines.length - 1) * 11 + 6} textAnchor="middle" fontSize="18" fontWeight="700" fill={data.focus ? '#6b3a5e' : '#b9a8bd'} className="font-heading" data-testid="fishbone-focus">
          {(focusLines.length ? focusLines : ['Main focus']).map((l, i) => <tspan key={i} x="890" dy={i ? 22 : 0}>{l}</tspan>)}
        </text>
      </svg>
    </div>
  );
}
